import * as React from "react";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";

import { cn } from "@/lib/utils";
import { ButtonProps } from "@/components/ui/button";

const Pagination = ({ className, ...props }: React.ComponentProps<"nav">) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto flex w-full ", className)}
    {...props}
  />
);
Pagination.displayName = "Pagination";

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn(
      "flex flex-row items-center gap-mob-10 sm:gap-tab-10 lg:gap-web-10 w-full ",
      className,
    )}
    {...props}
  />
));
PaginationContent.displayName = "PaginationContent";

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => (
  <li ref={ref} {...props} className={className ? "block" : ""} />
));
PaginationItem.displayName = "PaginationItem";

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<ButtonProps, "size"> &
  React.ComponentProps<"a">;

const PaginationLink = ({
  // className,
  isActive,
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? "page" : undefined}
    className={`${isActive ? "bg-SecondaryYellow text-PrimaryLightBlue border-SecondaryYellow" : " bg-White text-PrimaryLightBlue border-PrimaryLightBlue"} flex items-center text-mob-16 sm:text-tab-18 lg:text-web-18 px-mob-15 sm:px-tab-20 lg:px-web-20 py-mob-7 sm:py-tab-9 lg:py-web-9 border`}
    {...props}
  />
);
PaginationLink.displayName = "PaginationLink";

const PaginationPrevious = ({
  isActive,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to previous page"
    className={`${isActive ? "bg-SecondaryYellow text-PrimaryLightBlue border-SecondaryYellow" : " bg-White text-PrimaryLightBlue border-PrimaryLightBlue"} flex justify-center items-center text-mob-16 sm:text-tab-18 lg:text-web-18 px-mob-20 sm:px-tab-15 lg:px-web-20 py-mob-7 sm:py-tab-9 lg:py-web-9 border `}
    {...props}
  >
    <ChevronLeft className="h-mob-20 w-mob-20 sm:h-tab-20 sm:w-tab-20 lg:h-web-20 lg:w-web-20" />
    <span>Previous</span>
  </PaginationLink>
);
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = ({
  isActive,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to next page"
    className={`${isActive ? "bg-SecondaryYellow text-PrimaryLightBlue border-SecondaryYellow" : " bg-White text-PrimaryLightBlue border-PrimaryLightBlue"} flex justify-center items-center text-mob-16 sm:text-tab-18 lg:text-web-18 px-mob-15 sm:px-tab-15 lg:px-web-20 py-mob-7 sm:py-tab-9 lg:py-web-9 border`}
    {...props}
  >
    <span>Next</span>
    <ChevronRight className="h-mob-20 w-mob-20 sm:h-tab-20 sm:w-tab-20 lg:h-web-20 lg:w-web-20" />
  </PaginationLink>
);
PaginationNext.displayName = "PaginationNext";

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn("flex  items-center justify-center text-PrimaryLightBlue", className)}
    {...props}
  >
    <MoreHorizontal className="h-mob-25 w-mob-25 sm:h-tab-25 sm:w-tab-25 lg:h-web-25 lg:w-web-25" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
